/**
 * P2: Multi-Stage Validation Gates Dashboard
 * 
 * Displays the status of quality gates (S1, S2, V1-V4) across the content pipeline.
 * Provides overview of source, AKU, and rack quality at a glance.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { 
  Shield,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Activity,
  Database,
  Layers,
  FileText,
  ChevronDown,
  ChevronUp,
  Info,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

// P0 FIX: Use same-origin API calls to avoid CORS issues in production
const API = ''; // Relative URL - browser uses same origin

// Gate status colors
const STATUS_COLORS = {
  PASSED: 'bg-green-100 text-green-800 border-green-300',
  BLOCKED: 'bg-red-100 text-red-800 border-red-300',
  WARNING: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  SKIPPED: 'bg-gray-100 text-gray-600 border-gray-300'
};

const STATUS_ICONS = {
  PASSED: CheckCircle,
  BLOCKED: XCircle,
  WARNING: AlertTriangle,
  SKIPPED: Info
};

// Health colors
const HEALTH_COLORS = {
  healthy: 'bg-green-500',
  warning: 'bg-yellow-500',
  critical: 'bg-red-500',
  unknown: 'bg-gray-400'
};

const GatesDashboard = ({ isActive = false }) => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [examProfileId, setExamProfileId] = useState('PSM1');
  const [examProfiles, setExamProfiles] = useState([]);
  
  // Data
  const [gateDefinitions, setGateDefinitions] = useState([]);
  const [pipelineOverview, setPipelineOverview] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  
  // Fetch exam profiles
  const fetchExamProfiles = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/exam-config/list`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setExamProfiles(data.exams || []);
      }
    } catch (error) {
      console.error('Error fetching exam profiles:', error);
    }
  }, [getAuthHeaders]);
  
  // Fetch gate definitions
  const fetchGateDefinitions = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/gates/definitions`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setGateDefinitions(data);
      }
    } catch (error) {
      console.error('Error fetching gate definitions:', error);
    }
  }, [getAuthHeaders]);
  
  // Fetch pipeline overview
  const fetchPipelineOverview = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API}/api/gates/pipeline-overview?exam_profile_id=${examProfileId}`,
        { headers: getAuthHeaders() }
      );
      
      if (response.ok) {
        const data = await response.json();
        setPipelineOverview(data);
      } else {
        toast.error('Fehler beim Laden der Pipeline-Übersicht');
      }
    } catch (error) {
      console.error('Error fetching pipeline overview:', error);
      toast.error('Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, examProfileId]);
  
  // Initial load
  useEffect(() => {
    if (isActive && !initialLoadDone) {
      fetchExamProfiles();
      fetchGateDefinitions();
      fetchPipelineOverview();
      setInitialLoadDone(true);
    }
  }, [isActive, initialLoadDone, fetchExamProfiles, fetchGateDefinitions, fetchPipelineOverview]);
  
  // Refresh when exam profile changes
  useEffect(() => {
    if (initialLoadDone) {
      fetchPipelineOverview();
    }
  }, [examProfileId, initialLoadDone, fetchPipelineOverview]);
  
  // Calculate progress for a gate
  const calculateProgress = (passed, total) => {
    if (total === 0) return 0;
    return Math.round((passed / total) * 100);
  };
  
  // Get health badge
  const HealthBadge = ({ health }) => (
    <div className="flex items-center gap-2">
      <div className={`w-3 h-3 rounded-full ${HEALTH_COLORS[health] || HEALTH_COLORS.unknown}`} />
      <span className="text-sm font-medium capitalize">
        {health === 'healthy' ? 'Gesund' :
         health === 'warning' ? 'Warnung' :
         health === 'critical' ? 'Kritisch' : 'Unbekannt'}
      </span>
    </div>
  );
  
  // Gate card component
  const GateCard = ({ gateId, name, description, entityType, counts }) => {
    const passedKey = `passed_${gateId.toLowerCase()}`;
    const passed = counts?.[passedKey] || 0;
    const total = counts?.total || 0;
    const blocked = counts?.blocked || 0;
    const progress = calculateProgress(passed, total);
    
    return (
      <div className="p-4 bg-white rounded-lg border shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="font-mono">{gateId}</Badge>
            <span className="font-medium text-sm">{name}</span>
          </div>
          {progress === 100 ? (
            <CheckCircle className="w-5 h-5 text-green-500" />
          ) : blocked > 0 ? (
            <XCircle className="w-5 h-5 text-red-500" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
          )}
        </div>
        <p className="text-xs text-gray-500 mb-3">{description}</p>
        <div className="space-y-2">
          <div className="flex justify-between text-xs">
            <span>Bestanden</span>
            <span>{passed} / {total}</span>
          </div>
          <Progress value={progress} className="h-2" />
          {blocked > 0 && (
            <p className="text-xs text-red-600">
              {blocked} blockiert
            </p>
          )}
        </div>
      </div>
    );
  };
  
  // Section component
  const PipelineSection = ({ title, icon: Icon, entityType, data, gates }) => {
    const isExpanded = expandedSection === entityType;
    
    return (
      <Card className="mb-4">
        <CardHeader
          className="cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setExpandedSection(isExpanded ? null : entityType)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Icon className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <CardTitle className="text-lg">{title}</CardTitle>
                <CardDescription>
                  {data?.total || 0} Einträge | {data?.blocked || 0} blockiert
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {data?.blocked > 0 ? (
                <Badge className="bg-red-100 text-red-800">
                  {data.blocked} Probleme
                </Badge>
              ) : (
                <Badge className="bg-green-100 text-green-800">
                  OK
                </Badge>
              )}
              {isExpanded ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </div>
          </div>
        </CardHeader>
        
        {isExpanded && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {gates.map((gate) => (
                <GateCard
                  key={gate.gate_id}
                  gateId={gate.gate_id}
                  name={gate.name}
                  description={gate.description}
                  entityType={entityType}
                  counts={data}
                />
              ))}
            </div>
          </CardContent>
        )}
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="gates-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Quality Gates</h2>
          <p className="text-sm text-gray-500 mt-1">
            Multi-Stage Validation Gates (S1, S2, V1-V4) für die Content-Pipeline
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={examProfileId} onValueChange={setExamProfileId}>
            <SelectTrigger className="w-40" data-testid="exam-profile-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {examProfiles.map((exam) => (
                <SelectItem key={exam.exam_id} value={exam.exam_id}>
                  {exam.exam_id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button
            variant="outline"
            size="sm"
            onClick={fetchPipelineOverview}
            disabled={loading}
            data-testid="refresh-gates"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>
      </div>
      
      {/* Overall Health Banner */}
      {pipelineOverview && (
        <Card className={`${
          pipelineOverview.overall_health === 'healthy' ? 'border-green-200 bg-green-50' :
          pipelineOverview.overall_health === 'warning' ? 'border-yellow-200 bg-yellow-50' :
          pipelineOverview.overall_health === 'critical' ? 'border-red-200 bg-red-50' :
          'border-gray-200'
        }`}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Shield className={`w-8 h-8 ${
                  pipelineOverview.overall_health === 'healthy' ? 'text-green-600' :
                  pipelineOverview.overall_health === 'warning' ? 'text-yellow-600' :
                  pipelineOverview.overall_health === 'critical' ? 'text-red-600' :
                  'text-gray-400'
                }`} />
                <div>
                  <h3 className="font-semibold text-lg">
                    Pipeline Status: {examProfileId}
                  </h3>
                  <HealthBadge health={pipelineOverview.overall_health} />
                </div>
              </div>
              <div className="text-right text-sm text-gray-500">
                Letzte Prüfung:<br />
                {new Date(pipelineOverview.checked_at).toLocaleString('de-DE')}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Gate Definitions Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Info className="w-4 h-4" />
            Gate-Definitionen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-20">Gate</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Beschreibung</TableHead>
                <TableHead>Anwendbar auf</TableHead>
                <TableHead className="w-20">Gewicht</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {gateDefinitions.map((gate) => (
                <TableRow key={gate.gate_id}>
                  <TableCell>
                    <Badge variant="outline" className="font-mono">
                      {gate.gate_id}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium">{gate.name}</TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {gate.description}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {gate.applicable_to.map((type) => (
                        <Badge key={type} variant="secondary" className="text-xs">
                          {type}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>{gate.weight.toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      
      {/* Pipeline Sections */}
      {pipelineOverview && (
        <>
          <PipelineSection
            title="Sources"
            icon={Database}
            entityType="sources"
            data={pipelineOverview.sources}
            gates={gateDefinitions.filter(g => g.applicable_to.includes('source'))}
          />
          
          <PipelineSection
            title="AKUs (Atomic Knowledge Units)"
            icon={Layers}
            entityType="akus"
            data={pipelineOverview.akus}
            gates={gateDefinitions.filter(g => g.applicable_to.includes('aku'))}
          />
          
          <PipelineSection
            title="Racks (Question Templates)"
            icon={FileText}
            entityType="racks"
            data={pipelineOverview.racks}
            gates={gateDefinitions.filter(g => g.applicable_to.includes('rack'))}
          />
        </>
      )}
      
      {/* Loading State */}
      {loading && !pipelineOverview && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      )}
      
      {/* Empty State */}
      {!loading && !pipelineOverview && (
        <Card>
          <CardContent className="py-12 text-center">
            <Activity className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-600">Keine Daten verfügbar</h3>
            <p className="text-sm text-gray-500 mt-1">
              Wählen Sie ein Exam-Profil und klicken Sie auf Aktualisieren.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default GatesDashboard;
